import { GateUsageScheduleResourceFragment } from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React, { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { DatasetObjectFieldSummary } from '../../components/DatasetFieldsSummary';
import { ContainerMap } from '../../components/layout/ContainerMap';
import LoadingIndicator from '../../components/LoadingIndicator';
import StageContainer from '../../layout/stage/StageContainer';
import { analyzeGateUsageSchedule } from '../store/analyze.state';

import { scheduleHiddenDocksState } from './schedule.state';
import SimulationJobsTimelineLayer from './SimulationJobsTimelineLayer';
import SimulationJobTimelineDataTable, {
  JobTimelineDataTableRow,
} from './SimulationJobTimelineDataTable';
import TimelineJobTooltip from './TimelineJobTooltip';
import useSimulationJobTimelineTableConfig from './useSimulationJobTimelineTableConfig';
import WaitTimeClassificationLegend from './WaitTimeClassificationLegend';

function getRows(
  resources: GateUsageScheduleResourceFragment[],
): JobTimelineDataTableRow[] {
  return _(resources)
    .map(res => _.map(res.assignments, a => ({ ...a, resourceId: res.id })))
    .flatten()
    .value();
}

const SimulationJobsTimeline: React.FC = () => {
  const { t } = useTranslation('simulation');

  const [hiddenDocks, setHiddenDocks] = useRecoilState(
    scheduleHiddenDocksState,
  );
  const schedule = useRecoilValue(analyzeGateUsageSchedule);
  const columnsConfig = useSimulationJobTimelineTableConfig();
  const rawData = getRows(schedule?.resources);

  // Ensure hiddenDocks is always a Set with proper type checking
  const safeHiddenDocks = useMemo(() => {
    // Handle cases where persistent storage might return unexpected types
    if (!hiddenDocks) return new Set<string>();
    if (!Array.isArray(hiddenDocks)) {
      console.warn('scheduleHiddenDocksState is not an array:', hiddenDocks);
      return new Set<string>();
    }
    return new Set(hiddenDocks);
  }, [hiddenDocks]);

  // Generate all field summaries
  const allFieldsSummary = useMemo((): DatasetObjectFieldSummary[] => {
    if (!rawData?.length) return [];

    const summaries = columnsConfig.map(column => {
      const fieldName = column.field as string;
      const allValues = rawData.map(row => _.get(row, fieldName));
      const nonNullValues = allValues.filter(v => v != null && v !== '');
      const distinctValues = new Set(nonNullValues);
      const distinctCount = distinctValues.size;

      const summary: DatasetObjectFieldSummary = {
        name: fieldName,
        hasData: nonNullValues.length > 0,
        distinctValueCount: distinctCount,
      };

      // Add specific statistics based on field type
      if (fieldName === 'resourceId') {
        summary.valueCounters = _.countBy(rawData, 'resourceId');
      } else if (fieldName === 'job.duration') {
        const durations = nonNullValues
          .map(v => Number(v))
          .filter(v => !isNaN(v));
        if (durations.length > 0) {
          summary.numberRange = [_.min(durations), _.max(durations)];
          summary.totalSum = _.sum(durations);
        }
      } else if (fieldName === 'jobDetails.details.picklistCount') {
        const counts = nonNullValues.map(v => Number(v)).filter(v => !isNaN(v));
        if (counts.length > 0) {
          summary.numberRange = [_.min(counts), _.max(counts)];
          summary.totalSum = _.sum(counts);
        }
      } else if (fieldName === 'jobDetails.details.picklistLineCount') {
        const counts = nonNullValues.map(v => Number(v)).filter(v => !isNaN(v));
        if (counts.length > 0) {
          summary.numberRange = [_.min(counts), _.max(counts)];
          summary.totalSum = _.sum(counts);
        }
      } else if (fieldName === 'jobDetails.details.orderCount') {
        const counts = nonNullValues.map(v => Number(v)).filter(v => !isNaN(v));
        if (counts.length > 0) {
          summary.numberRange = [_.min(counts), _.max(counts)];
          summary.totalSum = _.sum(counts);
        }
      } else if (fieldName === 'jobDetails.details.netWeight') {
        const weights = nonNullValues
          .map(v => Number(v))
          .filter(v => !isNaN(v));
        if (weights.length > 0) {
          summary.numberRange = [_.min(weights), _.max(weights)];
          summary.totalSum = _.sum(weights);
        }
      } else if (fieldName === 'jobDetails.details.volume') {
        const volumes = nonNullValues
          .map(v => Number(v))
          .filter(v => !isNaN(v));
        if (volumes.length > 0) {
          summary.numberRange = [_.min(volumes), _.max(volumes)];
          summary.totalSum = _.sum(volumes);
        }
      }

      return summary;
    });

    return summaries;
  }, [rawData, columnsConfig]);

  // Extract dock stats for separate display
  const dockStats = useMemo(() => {
    const dockSummary = allFieldsSummary.find(f => f.name === 'resourceId');
    if (!dockSummary?.valueCounters) {
      return {
        distinctCount: 0,
        counts: [],
      };
    }
    const sortedCounts = _.orderBy(
      Object.entries(dockSummary.valueCounters),
      ([, count]) => count, // sort by count
      'desc',
    );
    return {
      distinctCount: dockSummary.distinctValueCount || 0,
      counts: sortedCounts,
    };
  }, [allFieldsSummary]);

  // Extract wait times for statistics in the legend
  const waitTimes = useMemo(() => {
    if (!rawData?.length) return [];
    return rawData.map(row => {
      const deadline = new Date(row.job.deadline);
      const startTime = new Date(row.startTime);
      return Math.max(0, (startTime.getTime() - deadline.getTime()) / 1000);
    });
  }, [rawData]);

  const toggleDockVisibility = (dock: string) => {
    setHiddenDocks(prev => {
      const currentArray = prev || [];
      const currentSet = new Set(currentArray);
      if (currentSet.has(dock)) {
        return currentArray.filter(d => d !== dock);
      } else {
        return [...currentArray, dock];
      }
    });
  };

  return (
    <Suspense fallback={<LoadingIndicator message={t`Scheduling...`} />}>
      <WaitTimeClassificationLegend
        waitTimes={waitTimes}
        showStatistics={true}
        className="bg-app-panel-dark/50 rounded p-4"
      />
      <ContainerMap>
        <StageContainer type="jobs-schedule" darkBg={true}>
          <SimulationJobsTimelineLayer
            stageId="jobs-schedule"
            hiddenDocks={safeHiddenDocks}
          />
        </StageContainer>
        <TimelineJobTooltip stageId="jobs-schedule" />
      </ContainerMap>

      <SimulationJobTimelineDataTable
        hiddenDocks={safeHiddenDocks}
        toggleDockVisibility={toggleDockVisibility}
        allFieldsSummary={allFieldsSummary}
        rawData={rawData}
      />
    </Suspense>
  );
};

export default SimulationJobsTimeline;
